import { TooltipProvider } from "@repo/ui/components/ui/tooltip"
import { TokenRefreshUnavailableError } from "@repo/web-auth"
import * as Sentry from "@sentry/react"
import { captureException, withScope } from "@sentry/react"
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { RouterProvider, createRouter } from "@tanstack/react-router"
import { RowData } from "@tanstack/react-table"
import { isAxiosError } from "axios"
import { StrictMode } from "react"
import ReactDOM from "react-dom/client"
import Default404 from "./components/404"
import ErrorComponent from "./components/error-component"
import env from "./lib/env"
import { routeTree } from "./routeTree.gen"
import "./styles/globals.css"

if (env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    integrations: [Sentry.extraErrorDataIntegration()],
  })
}

const DEFAULT_QUERY_RETRIES = 3

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Auth failures are owned by the api interceptor, which refreshes and
      // replays once. Retrying them here only burns three more requests with
      // the same rejected token.
      retry: (failureCount, error) => {
        if (isAxiosError(error)) {
          const status = error.response?.status
          if (status === 401 || status === 403) return false
        }
        return failureCount < DEFAULT_QUERY_RETRIES
      },
    },
  },
  mutationCache: new MutationCache({
    onError: async (err) => {
      // The token source already reports this once per incident.
      if (err instanceof TokenRefreshUnavailableError) return
      captureException(err)
    },
  }),
  queryCache: new QueryCache({
    onError: async (err, query) => {
      if (err instanceof TokenRefreshUnavailableError) return

      withScope((scope) => {
        scope.setContext("query", { queryHash: query.queryHash })
        scope.setFingerprint([query.queryHash.replace(/[0-9]/g, "0")])
        captureException(err)
      })
    },
  }),
})

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: () => <ErrorComponent />,
  defaultNotFoundComponent: () => <Default404 />,
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    label?: string
    sticky?: boolean
  }
}

const rootElement = document.getElementById("app")

if (rootElement && !rootElement.innerHTML) {
  ReactDOM.createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <RouterProvider router={router} />
        </TooltipProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
}
